import { useCallback, useRef, useState } from 'react';

export type PortalTheme = 'light' | 'dark';

/** The four theme attributes a portalled surface carries (DESIGN.md §16.2). */
export interface PortalAttributes {
  'data-theme'?: PortalTheme;
  'data-accent'?: string;
  'data-backdrop'?: string;
  'data-glass'?: string;
}

export interface PortalThemeBinding {
  /**
   * Attach to an element that sits where the surface visually belongs — normally the trigger.
   * Radix forwards a ref through `asChild`, so this works on a wrapped trigger too.
   */
  anchorRef: (node: HTMLElement | null) => void;
  /** Call as the surface is opening. */
  capture: () => void;
  /** Put on the portalled element as `data-theme={theme}`. `undefined` until first capture. */
  theme: PortalTheme | undefined;
  /**
   * All four attributes, to spread on the portalled element: `{...portal.attributes}`. Empty
   * until first capture.
   */
  attributes: PortalAttributes;
}

const AXES = ['data-accent', 'data-backdrop', 'data-glass'] as const;

/**
 * The theme a portalled surface has to be drawn in.
 *
 * Radix portals to `document.body`, which is OUTSIDE every `[data-theme]` subtree — and /design
 * renders the gallery once inside `data-theme="light"` and once inside `data-theme="dark"`. A popup
 * opened from the dark pane came back in the APPLICATION's mode: a white panel over a dark page.
 * Atle screenshotted exactly that.
 *
 * So the trigger reads the nearest owner of each attribute above itself and the portalled content
 * is stamped with it. Since Glass 2 there are four (mode, accent, backdrop, glass). `data-theme`
 * is the one that matters most: `tokens.generated.css` recomputes every colour on an element that
 * carries it, from the accent/backdrop/glass values it inherits — and a portal in `document.body`
 * inherits those from `<html>`. Stamping the other three as well is what keeps a portal right
 * inside a subtree that sets its own (the Appearance preview), and it costs nothing.
 *
 * Reading at OPEN time rather than at mount keeps it right after the user changes the theme with
 * the popup closed.
 */
export function usePortalTheme(): PortalThemeBinding {
  const anchor = useRef<HTMLElement | null>(null);
  const [attributes, setAttributes] = useState<PortalAttributes>({});

  const anchorRef = useCallback((node: HTMLElement | null) => {
    anchor.current = node;
  }, []);

  const capture = useCallback(() => {
    const from = anchor.current ?? document.documentElement;
    const next: PortalAttributes = {
      'data-theme':
        from.closest('[data-theme]')?.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
    };
    for (const name of AXES) {
      const value = from.closest(`[${name}]`)?.getAttribute(name);
      if (value !== null && value !== undefined) next[name] = value;
    }
    setAttributes(next);
  }, []);

  return { anchorRef, capture, theme: attributes['data-theme'], attributes };
}
